import React, { useEffect, useContext, useState } from 'react';
import type { FC, PropsWithChildren } from 'react';

import { AppContext } from '../../contextStore/AppContext';
import { TRANSLATION_CLASS_NAMES } from '../../../utils/element-ids';
import useEnvironmentContextProvider from '../../contextStore/EnvironmentContext/useEnvironmentContextProvider';

import useRenderedLanguage from './utils/useRenderedLanguage';

declare global {
  interface Window {
    OneLinkMoxieJS: any;
  }
}

const LanguageSelector: FC<PropsWithChildren> = ({
  children,
}) => {
  const { setData } = useContext(AppContext);
  const { renderedContainerElement } =
    useEnvironmentContextProvider();

  const { renderedLanguage, translationKey } =
    useRenderedLanguage();

  const [moxieTranslated, setMoxieTranslated] = useState<
    string | null
  >(null);

  const updateMoxieTranslatedValue = () => {
    const metaTag = document.querySelector(
      "meta[name='moxietranslated']",
    );
    const newMoxieTranslatedValue = metaTag
      ? metaTag.getAttribute('content')
      : null;

    // Only update state if the value has changed
    if (newMoxieTranslatedValue !== moxieTranslated) {
      setMoxieTranslated(newMoxieTranslatedValue);
    }
  };

  useEffect(() => {
    if (moxieTranslated && renderedContainerElement) {
      renderedContainerElement.classList.add(
        TRANSLATION_CLASS_NAMES.TRANSLATION_CLASS_NAME,
      );
    }
  }, [moxieTranslated, renderedContainerElement]);

  useEffect(() => {
    let observer: MutationObserver | null = null;
    if (
      renderedLanguage &&
      renderedLanguage !== 'en' &&
      translationKey
    ) {
      setData({ overlayLoaderConfig: { show: true } });
      const tKey =
        renderedLanguage === 'es' ? translationKey : '';
      const oneLinkScript = document.createElement('script');
      oneLinkScript.src =
        'https://www.onelink-edge.com/moxie.min.js';
      oneLinkScript.setAttribute('data-oljs', tKey);
      oneLinkScript.type = 'text/javascript';
      oneLinkScript.referrerPolicy =
        'no-referrer-when-downgrade';
      document.body.appendChild(oneLinkScript);

      // Set up a MutationObserver to observe changes to the <head> element
      observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Check if the mutations include changes to `moxietranslated`
          if (
            mutation.type === 'childList' ||
            mutation.type === 'attributes'
          ) {
            updateMoxieTranslatedValue();
          }
        });
      });

      // Start observing
      observer.observe(document.head, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['content'],
      });

      // Initial check for the meta tag
      updateMoxieTranslatedValue();
    }

    // Cleanup function to disconnect the observer when the component unmounts
    return () => {
      if (renderedLanguage !== 'en' && observer) {
        observer.disconnect();
      }
    };
  }, [renderedLanguage, translationKey]);
  return (
    <>
      {renderedLanguage === 'en' || moxieTranslated
        ? children
        : null}
    </>
  );
};

export default LanguageSelector;
